import React, { useMemo } from "react";
import { renderToHTMLString } from "@tiptap/static-renderer";
import parse from "html-react-parser";
import { getBaseExtensions } from "../qureal_editor/plugins/EditorConfig";
import styles from "./styles/QurealRenderer.module.css";
import { css, Global } from "@emotion/react";

/**
 * Renders TipTap JSON content as HTML using static renderer
 * @param {Object} props - Component props
 * @param {Object|string} props.content - JSON content from TipTap or HTML string
 * @param {boolean} props.darkMode - Whether to use dark mode styling
 * @param {Object} props.className - Additional CSS classes to apply
 */
const QurealRenderer = ({
    content,
    darkMode = false,
    className = "",
    fontSize = 1,
    lineHeight = 1.5,
    letterSpacing = "normal",
    elementSpacing = 1,
    colorContent,
    colorHeadings,
    fontFamilyHeadings,
    bulletStyle = "disc",
}) => {
    const extensions = useMemo(() => getBaseExtensions(), []);

    const html = useMemo(() => {
        if (!content) return "";

        // If content is a string (HTML), return directly
        if (typeof content === "string") {
            return content;
        }

        // If content is JSON, use the static renderer
        try {
            return renderToHTMLString({
                content: content,
                extensions,
            });
        } catch (error) {
            console.error("Error generating HTML with static renderer:", error);
            return "";
        }
    }, [content, extensions]);

    // Parse HTML to React elements safely
    const parsedContent = useMemo(() => {
        return parse(html);
    }, [html]);

    const rendererStyles = useMemo(
        () => css`
            .${styles.rendererContent} {
                font-size: ${fontSize}em !important;
            }
            /* Reset all default margins first */
            .${styles.rendererContent} * {
                margin-top: 0;
                margin-bottom: 0;
            }

            /* Apply consistent spacing to all block elements */
            .${styles.rendererContent} p,
            .${styles.rendererContent} ul,
            .${styles.rendererContent} ol,
            .${styles.rendererContent} blockquote,
            .${styles.rendererContent} pre,
            .${styles.rendererContent} h1,
            .${styles.rendererContent} h2,
            .${styles.rendererContent} h3,
            .${styles.rendererContent} h4,
            .${styles.rendererContent} h5,
            .${styles.rendererContent} h6,
            .${styles.rendererContent} table,
            .${styles.rendererContent} img,
            .${styles.rendererContent} figure,
            .${styles.rendererContent} video,
            .${styles.rendererContent} iframe,
            .${styles.rendererContent} hr {
                margin-top: ${elementSpacing}em !important;
                margin-bottom: ${elementSpacing}em !important;
            }
            /* Remove top margin from first child to avoid double spacing */
            .${styles.rendererContent} > *:first-child {
                margin-top: 0 !important;
            }

            /* Remove bottom margin from last child to avoid extra space */
            .${styles.rendererContent} > *:last-child {
                margin-bottom: 0 !important;
            }
            .${styles.rendererContent} li p {
                margin-top: 0.25em !important;
                margin-bottom: 0.25em !important;
            }
            .${styles.rendererContent} li ul,
            .${styles.rendererContent} li ol {
                margin-top: 0.25em !important;
                margin-bottom: 0.25em !important;
            }

            /* Text styles */
            .${styles.rendererContent} p,
            .${styles.rendererContent} ul,
            .${styles.rendererContent} ol,
            .${styles.rendererContent} blockquote,
            .${styles.rendererContent} pre,
            .${styles.rendererContent} pre code {
                line-height: ${lineHeight * 100}% !important;
                letter-spacing: ${letterSpacing} !important;
                color: ${colorContent || "inherit"} !important;
            }
            /* Heading styles */
            .${styles.rendererContent} h1,
            .${styles.rendererContent} h2,
            .${styles.rendererContent} h3,
            .${styles.rendererContent} h4,
            .${styles.rendererContent} h5,
            .${styles.rendererContent} h6 {
                font-family: ${fontFamilyHeadings || "inherit"} !important;
                color: ${colorHeadings || "inherit"} !important;
            }

            /* List specific styles */
            .${styles.rendererContent} ul {
                list-style-type: ${bulletStyle} !important;
            }

            /* Code block specific styles */
            .${styles.rendererContent} pre {
                line-height: ${lineHeight * 100}% !important;
                white-space: pre-wrap !important;
            }
        `,
        [
            fontSize,
            lineHeight,
            letterSpacing,
            elementSpacing,
            colorContent,
            colorHeadings,
            fontFamilyHeadings,
            bulletStyle,
        ]
    );
    return (
        <>
            <Global styles={rendererStyles} />
            <div
                className={`${styles.rendererWrapper} ${
                    darkMode ? styles.darkMode : ""
                } ${className}`}
            >
                <div className={styles.rendererContent}>{parsedContent}</div>
            </div>
        </>
    );
};

export default QurealRenderer;
